import React, { useState, useEffect } from "react";
import TopbarNav from "../../components/TopbarNav";
import SidebarNav from "../../components/MasterSideBar";
import { updateStaff, getOneStaff } from "../../api/staff-calls-api";
import Select from "react-select";
import countries from "../../utils/countryList";
import { FiPlus, FiSave, FiUser, FiBriefcase, FiCreditCard, FiBookOpen, FiUsers, FiChevronDown, FiChevronUp } from "react-icons/fi";

import axios from "axios";
import { toast } from "react-toastify";

const countryOptions = countries.map((c) => ({ label: `${c.flag} ${c.name}`, value: c.name }));

const Section = ({ title, icon, children }) => {
    const [isOpen, setIsOpen] = useState(true);
    return (
        <div className="bg-white border border-gray-100 rounded-2xl mb-6 overflow-hidden shadow-sm transition-all">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full text-left px-6 py-4 bg-gray-50/50 font-bold text-gray-700 flex justify-between items-center group"
            >
                <div className="flex items-center gap-3 uppercase tracking-wider text-xs">
                    <span className="p-2 bg-white rounded-lg text-orange-600 shadow-sm">{icon}</span>
                    {title}
                </div>
                <span className="text-gray-400 group-hover:text-orange-600 transition-colors">
                    {isOpen ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />}
                </span>
            </button>
            {isOpen && (
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in slide-in-from-top-2 duration-300">
                    {children}
                </div>
            )}
        </div>
    );
};

const PDSettingsPage = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const staffId = user?.id;
    const token = localStorage.getItem("token");

    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const formatDate = (dateString) => (dateString ? new Date(dateString).toISOString().split("T")[0] : "");

    useEffect(() => {
        async function fetchStaff() {
            try {
                const res = await getOneStaff(staffId);
                const data = res.data || res;
                setFormData({
                    ...data,
                    dateOfBirth: formatDate(data.dateOfBirth),
                    villageOrBlock: data.residence?.villageOrBlock || "",
                    payam: data.residence?.payam || "",
                    county: data.residence?.county || "",
                    state: data.residence?.state || "",
                    dateOfEmployment: formatDate(data.dateOfEmployment),
                    bankName: data.bankingDetails?.[0]?.bankName || "",
                    bankBranch: data.bankingDetails?.[0]?.bankBranch || "",
                    accountName: data.bankingDetails?.[0]?.accountName || "",
                    accountNumber: data.bankingDetails?.[0]?.accountNumber || "",
                    currency: data.bankingDetails?.[0]?.currency || "",
                    swiftCode: data.bankingDetails?.[0]?.swiftCode || "",
                    institution: data.qualifications?.[0]?.institution || "",
                    award: data.qualifications?.[0]?.award || "",
                    yearFrom: data.qualifications?.[0]?.yearFrom || "",
                    yearTo: data.qualifications?.[0]?.yearTo || "",
                    keyExpertise: data.workHistory?.keyExpertise || "",
                    recentEmployer: data.workHistory?.recentEmployer || "",
                    workrole: data.workHistory?.workrole || "",
                    periodOfService: data.workHistory?.periodOfService || "",
                    recentGrossAnnualPaymentRange: data.workHistory?.recentGrossAnnualPaymentRange || "",
                    nokName: data.nextOfKin?.name || "",
                    nokSex: data.nextOfKin?.sex || "",
                    nokNationality: data.nextOfKin?.nationality || "",
                    nokRelationship: data.nextOfKin?.relationship || "",
                    nokVillageOrBlock: data.nextOfKin?.residence?.villageOrBlock || "",
                    nokPayam: data.nextOfKin?.residence?.payam || "",
                    nokCounty: data.nextOfKin?.residence?.county || "",
                    nokState: data.nextOfKin?.residence?.state || "",
                    nokTel: data.nextOfKin?.tel || "",
                    nokMobile: data.nextOfKin?.mobile || "",
                    nokEmail: data.nextOfKin?.email || "",
                });
            } catch (err) {
                toast.error("Failed to load profile");
            } finally {
                setLoading(false);
            }
        }
        fetchStaff();
    }, [staffId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...formData,
                residence: {
                    villageOrBlock: formData.villageOrBlock,
                    payam: formData.payam,
                    county: formData.county,
                    state: formData.state,
                },
                bankingDetails: [{
                    bankName: formData.bankName,
                    bankBranch: formData.bankBranch,
                    accountName: formData.accountName,
                    accountNumber: formData.accountNumber,
                    currency: formData.currency,
                    swiftCode: formData.swiftCode,
                }],
                qualifications: [{
                    institution: formData.institution,
                    award: formData.award,
                    yearFrom: formData.yearFrom,
                    yearTo: formData.yearTo,
                }],
                workHistory: {
                    keyExpertise: formData.keyExpertise,
                    recentEmployer: formData.recentEmployer,
                    workrole: formData.workrole,
                    periodOfService: formData.periodOfService,
                    recentGrossAnnualPaymentRange: formData.recentGrossAnnualPaymentRange,
                },
                nextOfKin: {
                    name: formData.nokName,
                    sex: formData.nokSex,
                    nationality: formData.nokNationality,
                    relationship: formData.nokRelationship,
                    residence: {
                        villageOrBlock: formData.nokVillageOrBlock,
                        payam: formData.nokPayam,
                        county: formData.nokCounty,
                        state: formData.nokState,
                    },
                    tel: formData.nokTel,
                    mobile: formData.nokMobile,
                    email: formData.nokEmail,
                },
            };
            await updateStaff(staffId, payload);
            toast.success("Profile updated successfully");
        } catch (err) {
            toast.error("Failed to update profile");
        } finally {
            setSaving(false);
        }
    };

    // handleResignationSubmit removed

    return (
        <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
            <SidebarNav visible={sidebarOpen} onClose={() => setSidebarOpen(false)} />

            <div className="flex-1 flex flex-col md:ml-64 transition-all duration-300 min-w-0">
                <TopbarNav onSidebarToggle={() => setSidebarOpen(true)} />

                <main className="p-4 md:p-8 mt-16 lg:mt-20 space-y-8 overflow-y-auto">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl md:text-3xl font-black text-gray-800 uppercase tracking-tight">My Settings</h1>
                            <p className="text-gray-500 text-sm mt-1">Manage your professional profile and personal records.</p>
                        </div>
                        {/* Resignation button removed */}
                    </div>

                    <form className="relative" onSubmit={handleSubmit}>
                        {loading ? (
                            <div className="py-20 text-center bg-white rounded-2xl border border-gray-100 shadow-sm">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mx-auto mb-4"></div>
                                <p className="text-gray-400 font-medium tracking-wide">Retrieving profile data...</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <Section title="Personal Information" icon={<FiUser />}>
                                    {renderField("First Name", "firstName", "text", null, true)}
                                    {renderField("Last Name", "lastName", "text", null, true)}
                                    {renderField("Email", "email", "email", null, true)}
                                    {renderField("Gender", "gender", "text", ["Male", "Female"], true)}
                                    {renderField("Date of Birth", "dateOfBirth", "date", null, true)}
                                    {renderField("Marital Status", "maritalStatus", "text", ["Single", "Married", "Other"])}
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Nationality</label>
                                        <Select
                                            options={countryOptions}
                                            value={countryOptions.find((opt) => opt.value === formData.nationality) || null}
                                            onChange={(sel) => setFormData({ ...formData, nationality: sel?.value || "" })}
                                            className="react-select-container shadow-sm"
                                            classNamePrefix="react-select"
                                            styles={{ control: (b) => ({ ...b, borderRadius: '10px', borderColor: '#f3f4f6' }) }}
                                        />
                                    </div>
                                    {renderField("National ID / Passport", "nationalIdOrPassport")}
                                    {renderField("State", "state")}
                                    {renderField("County", "county")}
                                    {renderField("Payam", "payam")}
                                    {renderField("Village / Block", "villageOrBlock")}
                                    {renderField("Telephone", "tel")}
                                    {renderField("Mobile", "mobile")}
                                </Section>

                                <Section title="Employment Details" icon={<FiBriefcase />}>
                                    {renderField("Job Title", "jobTitle", "text", null, true)}
                                    {renderField("Department", "department", "text", null, true)}
                                    {renderField("Employment Type", "employmentType", "text", null, true)}
                                    {renderField("Contract Duration", "contractDuration", "text", null, true)}
                                    {renderField("Date of Employment", "dateOfEmployment", "date", null, true)}
                                    {renderField("Salary Range", "salaryRange", "text", null, true)}
                                </Section>

                                <Section title="Banking Details" icon={<FiCreditCard />}>
                                    {renderField("Bank Name", "bankName")}
                                    {renderField("Bank Branch", "bankBranch")}
                                    {renderField("Account Name", "accountName")}
                                    {renderField("Account Number", "accountNumber")}
                                    {renderField("Currency", "currency")}
                                    {renderField("SWIFT Code", "swiftCode")}
                                </Section>

                                <Section title="Education & Work History" icon={<FiBookOpen />}>
                                    {renderField("Institution", "institution")}
                                    {renderField("Award", "award")}
                                    {renderField("Year From", "yearFrom")}
                                    {renderField("Year To", "yearTo")}
                                    {renderField("Key Expertise", "keyExpertise")}
                                    {renderField("Recent Employer", "recentEmployer")}
                                </Section>

                                <Section title="Next of Kin" icon={<FiUsers />}>
                                    {renderField("NOK Name", "nokName")}
                                    {renderField("Relationship", "nokRelationship")}
                                    {renderField("NOK Email", "nokEmail", "email")}
                                    {renderField("NOK Mobile", "nokMobile")}
                                </Section>

                                <div className="flex justify-end pt-6">
                                    <button 
                                        type="submit" 
                                        disabled={saving} 
                                        className="flex items-center gap-2 px-10 py-4 rounded-xl bg-orange-600 text-white font-bold hover:bg-orange-700 shadow-lg shadow-orange-200 transition-all active:scale-95 disabled:opacity-50"
                                    >
                                        <FiSave size={20} />
                                        {saving ? "Processing..." : "Save Professional Profile"}
                                    </button>
                                </div>
                            </div>
                        )}
                    </form>

                    {/* ResignationForm removed */}
                </main>
            </div>
        </div>
    );

    function renderField(label, name, type = "text", options, readOnly = false) {
        return (
            <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">{label}</label>
                {options ? (
                    <select
                        name={name}
                        value={formData[name] || ""}
                        onChange={handleChange}
                        className={`w-full border border-gray-100 rounded-xl px-4 py-2.5 text-sm transition-all shadow-sm outline-none focus:ring-2 focus:ring-orange-500/20 ${readOnly ? "bg-gray-50 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700"}`}
                        disabled={readOnly}
                    >
                        <option value="">-- Select --</option>
                        {options.map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
                    </select>
                ) : (
                    <input
                        name={name}
                        type={type}
                        value={formData[name] || ""}
                        onChange={handleChange}
                        className={`w-full border border-gray-100 rounded-xl px-4 py-2.5 text-sm transition-all shadow-sm outline-none focus:ring-2 focus:ring-orange-500/20 ${readOnly ? "bg-gray-50 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700 hover:border-gray-200"}`}
                        readOnly={readOnly}
                    />
                )}
            </div>
        );
    }
};

export default PDSettingsPage;